import type { ExtraFieldsConfig, SubjectKeyWithExtraFields } from '@/config/acl/utils/allow-read';
import { allowRead } from '@/config/acl/utils/allow-read';
import type { AclConfigHelpers, FilterableSubjectKey, SubjectConditions } from '@/types/acl-types';

type OptionsWithoutExtraFields<TKey extends FilterableSubjectKey> = {
    conditions?: SubjectConditions<TKey>;
};

type OptionsWithExtraFields<TKey extends SubjectKeyWithExtraFields> = {
    conditions?: SubjectConditions<TKey>;
    extraFields: ExtraFieldsConfig<TKey>;
};

export function allowFilterableSubject<TKey extends FilterableSubjectKey & SubjectKeyWithExtraFields>(
    { allow, restrict }: AclConfigHelpers,
    key: TKey,
    options: OptionsWithExtraFields<TKey>,
): void;

export function allowFilterableSubject<TKey extends Exclude<FilterableSubjectKey, SubjectKeyWithExtraFields>>(
    { allow, restrict }: AclConfigHelpers,
    key: TKey,
    options?: OptionsWithoutExtraFields<TKey>,
): void;

export function allowFilterableSubject<TKey extends FilterableSubjectKey>(
    helpers: AclConfigHelpers,
    key: TKey,
    options: OptionsWithoutExtraFields<TKey> | OptionsWithExtraFields<TKey & SubjectKeyWithExtraFields> = {},
) {
    const { allow, restrict } = helpers;
    const { conditions } = options;

    allow<TKey>('list', key, conditions);

    allow('filter', `${key}Filter`);

    if (conditions) {
        for (const filter of Object.keys(conditions) as Array<keyof SubjectConditions<`${TKey}Filter`>>) {
            if (Array.isArray(conditions[filter]) && (conditions[filter] as unknown[]).length === 1) {
                restrict('filter', `${key}Filter`, [filter]);
            }
        }
    }

    if ('extraFields' in options) {
        allowRead(helpers, key as SubjectKeyWithExtraFields & TKey, options.extraFields);
    } else {
        allow<TKey>('read', key);
    }
}
